import React, { useCallback, useState } from 'react';
import { Create, useNotify, useRedirect } from 'react-admin';
import { FormSpy } from 'react-final-form';
import { withStyles } from '@material-ui/core/styles';

import FormTitleBar from '../../core/components/FormTitleBar';
import CreateToolbar from '../../core/components/CreateToolbar';
import NavigationPrompt from '../../core/components/NavigationPrompt';
import LeaveConfirm from '../../core/components/LeaveConfirmWrapper';
import CustomSimpleForm from '../../core/components/form/CustomSimpleForm';
import useNotifyServerError from '../../core/hooks/useNotifyServerError';
import { getIcError } from '../../core/providers/utils';
import InvestorTagApi from '../../core/apis/InvestorTagApi';
import InvestorTagsAssociationsApi from '../../core/apis/InvestorTagsAssociationsApi';
import InvestorTagSummary from './InvestorTagSummary';

const styles = () => ({
  noActions: {
    marginTop: '0px'
  }
});

const InvestorTagTitle = () => {
  return (
    <FormTitleBar
      creation
      showAvatar
      type="Investor Tag"
      avatarContent="IT"
      recordLabel="Investor Tag"
    />
  );
};

const InvestorTagCreate = props => {
  const { classes } = props;

  const notify = useNotify();
  const redirect = useRedirect();
  const notifyServerError = useNotifyServerError();
  const [whenBlockNavigation, setWhenBlockNavigation] = useState(false);

  const allowNavigationOnSave = () => {
    setWhenBlockNavigation(false);
  };

  const handleFormIsDirty = useCallback(
    dirty => {
      // Block navigation when the form is dirty
      setWhenBlockNavigation(dirty);
    },
    [setWhenBlockNavigation]
  );

  const onSave = async values => {
    let hasPostError = false;
    const payload = { name: values.name, description: values.description };
    const data = await InvestorTagApi.create(payload).catch(err => {
      hasPostError = true;
      notifyServerError(err);
    });

    if (!hasPostError) {
      if (data.id && values.investorIds.length > 0) {
        const investorIdsToAdd = values.investorIds.map(id => {
          return { investorTagId: `${data.id}`, investorId: `${id}` };
        });
        InvestorTagsAssociationsApi.create(investorIdsToAdd)
          .then(() => {
            setWhenBlockNavigation(false);
            notify('ra.notification.created');
            redirect(`/investortags/${data.id}`);
          })
          .catch(err => {
            notify('ra.notification.server', {
              type: 'warning',
              messageArgs: {
                message:
                  'Investor Tag has been created without associations. ' +
                  getIcError(err),
                isForkedWarning: true
              }
            });
          });
      } else {
        setWhenBlockNavigation(false);
        notify('ra.notification.created');
        redirect(`/investortags/${data.id}`);
      }
    }
  };

  return (
    <React.Fragment>
      <NavigationPrompt when={whenBlockNavigation} creation>
        {(isOpen, onConfirm, onCancel) => (
          <LeaveConfirm
            isOpen={isOpen}
            onClose={onCancel}
            onConfirm={onConfirm}
          />
        )}
      </NavigationPrompt>
      <Create
        {...props}
        title={<InvestorTagTitle />}
        classes={{ noActions: classes.noActions }}
        record={{
          name: '',
          investorIds: [],
          description: ''
        }}
      >
        <CustomSimpleForm
          save={onSave}
          margin="normal"
          variant="standard"
          toolbar={
            <CreateToolbar allowNavigationOnSave={allowNavigationOnSave} />
          }
        >
          <FormSpy
            subscription={{ dirty: true, values: true }}
            onChange={e => handleFormIsDirty(e.dirty)}
          />
          <InvestorTagSummary creation />
        </CustomSimpleForm>
      </Create>
    </React.Fragment>
  );
};

export default withStyles(styles)(InvestorTagCreate);
